import { useMutation, useQueryClient } from "@tanstack/react-query";

import { updateUser } from "./utils";

import type { MutationConfig } from "@/lib/react-query";
import type { User } from "@/types/user";

export type UpdateAvatarInput = {
  avatarBlob: Blob;
  fileName: string;
};

type UseUpdateAvatarOptions = {
  mutationConfig?: MutationConfig<(data: UpdateAvatarInput) => Promise<User>>;
};

export const useUpdateAvatar = ({ mutationConfig }: UseUpdateAvatarOptions = {}) => {
  const queryClient = useQueryClient();

  const { onSuccess, ...restConfig } = mutationConfig || {};

  return useMutation({
    mutationFn: async ({ avatarBlob, fileName }: UpdateAvatarInput) => {
      const user = queryClient.getQueryData<User>(["profile"]);

      if (!user) throw new Error("User data not available");

      // convert blob to base64
      const base64Image = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(avatarBlob);
      });

      const request: Partial<User> = {
        additional_info: {
          ...user.additional_info,
          avatarImage: {
            name: fileName,
            url: fileName !== "" ? base64Image : "", // if fileName is empty, it means we want to remove the avatar, so we set url to empty string
          },
        },
      };

      return updateUser(request);
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      const previous = queryClient.getQueryData<User | undefined>(["profile"]);

      const merged: User = {
        ...(previous ?? (data as User)),
        ...(data ?? {}),
        additional_info: {
          ...(previous?.additional_info ?? {}),
          ...(data?.additional_info ?? {}),
        },
      } as User;

      queryClient.setQueryData(["profile"], merged);
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      onSuccess?.(data, variables, onMutateResult, context);
    },
    ...restConfig,
  });
};
